/**
 * 推荐/轮播图适配器
 * ⚠️ 降级实现 - 现在使用热度算法，以后接入 /api/featured
 */

import type { GameSummary } from '../api/types';
import type { AdapterResult } from './types';
import { FeatureStatus, DEFAULT_FEATURE_FLAGS } from './types';
import { apiClient } from './index';
import { FALLBACK_FEATURED_GAMES } from '../config/fallback-data';

/**
 * 计算游戏热度分数
 * 点击权重是浏览的2倍（GameSummary可能没有clickCount）
 */
function calculateHeatScore(game: GameSummary & { clickCount?: number }): number {
  return game.viewCount + ((game as any).clickCount || 0) * 2;
}

export const featuredAdapter = {
  /**
   * 获取推荐游戏（轮播图用）
   * 
   * 现在：⚠️ 降级 - 取热度最高的5个，随机排序
   * 以后：✅ 完整 - 调用 /api/featured
   */
  async getFeatured(): Promise<AdapterResult<GameSummary[]>> {
    // 检查是否有正式API（通过功能开关）
    if (DEFAULT_FEATURE_FLAGS.featuredAPI) {
      try {
        // 以后实现：const response = await apiClient.getFeatured();
        // return { data: response.data, status: FeatureStatus.AVAILABLE };
      } catch (error) {
        // API失败，降级处理
      }
    }

    // ⚠️ 降级方案：使用热度算法
    try {
      const response = await apiClient.listGames({ 
        sort: 'popular', 
        limit: 5 
      });

      const games = response.games;
      
      // 按热度排序，取前5，然后随机打乱
      const featured = games
        .sort((a: any, b: any) => calculateHeatScore(b) - calculateHeatScore(a))
        .slice(0, 5)
        .sort(() => Math.random() - 0.5);

      return {
        data: featured,
        status: FeatureStatus.DEGRADED,
        message: '当前展示热度最高的游戏',
        fallback: true,
      };
    } catch (error) {
      // API完全失败，使用默认数据
      return {
        data: FALLBACK_FEATURED_GAMES,
        status: FeatureStatus.DEGRADED,
        message: '使用默认推荐',
        fallback: true,
      };
    }
  },

  /**
   * 获取热门游戏列表
   * ✅ 完全支持 - 直接调用API
   */
  async getHotGames(limit: number = 10): Promise<AdapterResult<GameSummary[]>> {
    try {
      const response = await apiClient.listGames({ 
        sort: 'popular', 
        limit 
      });

      return {
        data: response.games,
        status: FeatureStatus.AVAILABLE,
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * 获取最新游戏列表
   * ✅ 完全支持 - 直接调用API
   */
  async getNewGames(limit: number = 10): Promise<AdapterResult<GameSummary[]>> {
    try {
      const response = await apiClient.listGames({ 
        sort: 'newest', 
        limit 
      });

      return {
        data: response.games,
        status: FeatureStatus.AVAILABLE,
      };
    } catch (error) {
      throw error;
    }
  },
};
